// Shared catalogs for property forms (NewPropertyView, PropertyView).
// Energy ratings are the post-2016 Hungarian classes; the dropdown also
// offers a free-form "Other…" so older A++..G ratings still fit through
// the backend's length+regex validation.

export const ENERGY_RATINGS = ['AA+', 'AA', 'BB', 'CC', 'DD', 'EE', 'FF', 'GG', 'HH', 'II', 'JJ'];

// Tag identifiers persisted in the unit row (the property column is a
// denormalized cache). Labels are localized via property.amenity.{key}.
// Chips render icon-less for a uniform v5 look, so no icon field is needed.
//
// Primary set = the search-driving filters shown in the chip grid by
// default. `smoking` is a house rule (not an amenity) so it's no longer
// offered here; `wifi` is in-unit and moved to the secondary disclosure.
export const AMENITY_CATALOG = [
  { key: 'parking' },
  { key: 'elevator' },
  { key: 'balcony' },
  { key: 'garden' },
  { key: 'ac' },
  { key: 'dishwasher' },
  { key: 'washing_machine' },
  { key: 'pets' },
  { key: 'storage' },
  { key: 'bike' },
  { key: 'concierge' },
];

// Secondary / less-common amenities, shown behind a "Further amenities"
// disclosure so the primary grid stays focused on the common filters.
// Keys here persist exactly like the primary ones.
export const AMENITY_CATALOG_SECONDARY = [
  { key: 'wifi' },
];

// The unit `type` enum, kept in sync with UnitCard.UNIT_TYPES and the
// backend UnitRepository::TYPES. Order here is the display order.
export const UNIT_TYPE_KEYS = ['apartment', 'house', 'parking', 'garage', 'storage', 'commercial', 'other'];

// Map a Hungarian title-deed `ownership_form` ("megnevezés", e.g. "lakás",
// "garázs", "tároló") onto our unit `type` enum. The deed wording is free
// text, so we match on substrings against the most specific buckets first
// and fall back to 'other' for anything we don't recognise (telek, nyaraló,
// üdülő, …) rather than guessing 'apartment'. Diacritics are normalised so
// "garázs"/"garazs" both hit.
const OWNERSHIP_FORM_RULES = [
  // [type, [keywords...]] — evaluated in order; first keyword hit wins.
  ['garage',    ['garazs', 'teremgarazs', 'gepkocsitarolo', 'gepjarmutarolo']],
  ['parking',   ['gepkocsibeallo', 'parkolo', 'gepkocsi-beallo', 'beallo']],
  ['storage',   ['tarolo', 'raktar', 'pince', 'padlas']],
  ['commercial',['uzlet', 'iroda', 'uzlethelyiseg', 'kereskedelmi', 'muhely', 'helyiseg']],
  // Detached house ("kivett lakóház, udvar" / "kivett lakóház és gazdasági
  // épület" / "családi ház"). Must precede the apartment bucket so the
  // generic 'haz' substring there doesn't swallow "lakóház" first.
  ['house',     ['lakohaz', 'csaladi haz', 'csaladihaz']],
  ['apartment', ['lakas', 'tarsashaz', 'haz', 'ingatlan']],
];

/**
 * @param {string} form  raw ownership_form / megnevezés from OCR
 * @returns {string|null} a UNIT_TYPE_KEYS value, or null when `form` is empty
 *   (caller should leave the existing/default type untouched on null).
 */
export function ownershipFormToUnitType(form) {
  const raw = (form == null ? '' : String(form)).trim().toLowerCase();
  if (raw === '') return null;
  // Strip Hungarian diacritics so keyword matching is accent-insensitive.
  const norm = raw
    .replace(/[áàâä]/g, 'a').replace(/[éèêë]/g, 'e').replace(/[íìî]/g, 'i')
    .replace(/[óòôöőð]/g, 'o').replace(/[úùûüű]/g, 'u');
  for (const [type, keywords] of OWNERSHIP_FORM_RULES) {
    if (keywords.some((kw) => norm.includes(kw))) return type;
  }
  return 'other';
}

/** Decode a property.amenities column (JSON string or already-array) into a list of tag keys. */
export function decodeAmenities(value) {
  if (Array.isArray(value)) return value.filter((s) => typeof s === 'string');
  if (typeof value !== 'string' || value === '') return [];
  try {
    const arr = JSON.parse(value);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}
